import { Flex } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { Text } from '../../../ui-components';
import {
    CheckBalanceModalLink,
    GiftCardLink,
    StrongText,
    TelLink,
} from '../components/GiftCardsFAQSection/GiftCardsFAQSectionUI';
import type { FAQItem, FAQSchemaItem } from '../components/GiftCardsFAQSection/GiftCardsFAQSection';

export const getFAQItems = (intl: IntlShape, onCheckBalanceClick: () => void): FAQItem[] => [
    {
        id: 'gift_cards.landing_page.faq.how_can_i_use_my_gift_card_or_e_gift_card',
        title: intl.formatMessage({
            id: 'gift_cards.landing_page.faq.how_can_i_use_my_gift_card_or_e_gift_card.title',
            defaultMessage: 'How can I use my gift card or e-gift card?',
        }),
        answer: (
            <FormattedMessage
                id="gift_cards.landing_page.faq.how_can_i_use_my_gift_card_or_e_gift_card.answer"
                defaultMessage="Your gift card can be used to purchase items or cooking classes at any of our retail locations or on <link>surlatable.com</link>. If you require assistance in redeeming your gift card, please contact our Customer Service Center at <tel>1-317-559-2041</tel>."
                values={{
                    link: (chunks: React.ReactNode[]): React.ReactElement =>
                        GiftCardLink(chunks, { href: 'https://surlatable.com', noUnderline: true }),
                    tel: TelLink,
                }}
            />
        ),
        answerPlainText: intl.formatMessage({
            id: 'gift_cards.landing_page.faq.how_can_i_use_my_gift_card_or_e_gift_card.answerPlan',
            defaultMessage:
                'Your gift card can be used to purchase items or cooking classes at any of our retail locations or on surlatable.com. If you require assistance in redeeming your gift card, please contact our Customer Service Center at 1-317-559-2041.',
        }),
    },
    {
        id: 'gift_cards.landing_page.faq.do_you_charge_shipping_for_gift_cards',
        title: intl.formatMessage({
            id: 'gift_cards.landing_page.faq.do_you_charge_shipping_for_gift_cards.title',
            defaultMessage: 'Do you charge shipping for gift cards?',
        }),
        answer: (
            <Flex direction="column" gap={2}>
                <Text as="p" type="body" size="base" display="block">
                    <FormattedMessage
                        id="gift_cards.landing_page.faq.traditional_gift_card.answer"
                        defaultMessage="<strong>Traditional Gift Card:</strong> No, we do not charge for shipping when you purchase a gift card for delivery within the 48 contiguous states and select standard delivery. Express delivery is available for $6.50, while overnight delivery costs $10.00. For shipments to Alaska and Hawaii, an additional charge of $7.50 will apply. Overnight orders must be placed by 2 PM Eastern to qualify for next-business-day delivery. Standard delivery orders are shipped via USPS and typically arrive within 7–10 days."
                        values={{
                            strong: StrongText,
                        }}
                    />
                </Text>
                <Text as="p" type="body" size="base" display="block">
                    <FormattedMessage
                        id="gift_cards.landing_page.faq.electronic_gift_card.answer"
                        defaultMessage="<strong>Electronic Gift Card:</strong> Electronic gift cards are emailed directly to the recipient and are not subject to delivery charges."
                        values={{
                            strong: StrongText,
                        }}
                    />
                </Text>
            </Flex>
        ),
        answerPlainText: intl.formatMessage({
            id: 'gift_cards.landing_page.faq.do_you_charge_shipping_for_gift_cards.answerPlainText',
            defaultMessage:
                'Traditional Gift Card: No, we do not charge for shipping when you purchase a gift card for delivery within the 48 contiguous states and select standard delivery. Express delivery is available for $6.50, while overnight delivery costs $10.00. For shipments to Alaska and Hawaii, an additional charge of $7.50 will apply. Overnight orders must be placed by 2 PM Eastern to qualify for next-business-day delivery. Standard delivery orders are shipped via USPS and typically arrive within 7–10 days. Electronic Gift Card: Electronic gift cards are emailed directly to the recipient and are not subject to delivery charges.',
        }),
    },
    {
        id: 'gift_cards.landing_page.faq.do_gift_cards_expire',
        title: intl.formatMessage({
            id: 'gift_cards.landing_page.faq.do_gift_cards_expire.title',
            defaultMessage: 'Do gift cards expire?',
        }),
        answer: intl.formatMessage({
            id: 'gift_cards.landing_page.faq.do_gift_cards_expire.answer',
            defaultMessage:
                'Our gift cards never expire and can be used for purchases in any of our stores, for cooking classes, on our website, and when ordering by phone. Gift cards cannot be redeemed for cash unless required by law. In California, Sur La Table gift cards with balances under $10 are eligible for cash redemption.',
        }),
    },
    {
        id: 'gift_cards.landing_page.faq.can_e_gift_cards_be_printed_and_gifted_to_someone',
        title: intl.formatMessage({
            id: 'gift_cards.landing_page.faq.can_e_gift_cards_be_printed_and_gifted_to_someone.title',
            defaultMessage: 'Can e-gift cards be printed and gifted to someone?',
        }),
        answer: intl.formatMessage({
            id: 'gift_cards.landing_page.faq.can_e_gift_cards_be_printed_and_gifted_to_someone.answer',
            defaultMessage:
                'Yes, when purchasing an e-gift card from our website, you can choose to send it to yourself instead of the gift recipient and print it.',
        }),
    },
    {
        id: 'gift_cards.landing_page.faq.how_do_i_check_my_gift_card_balance',
        title: intl.formatMessage({
            id: 'gift_cards.landing_page.faq.how_do_i_check_my_gift_card_balance.title',
            defaultMessage: 'How do I check my gift card balance?',
        }),
        answer: (
            <FormattedMessage
                id="gift_cards.landing_page.faq.how_do_i_check_my_gift_card_balance.answer"
                defaultMessage="You can check the balance of your Sur La Table gift card online, in stores, or by calling our Customer Service Center at <tel>1-317-559-2041</tel>. To check your balance online, click <checkBalanceModal>here</checkBalanceModal>, then enter your gift card number and PIN in the 'Gift Card Balance' fields."
                values={{
                    tel: TelLink,
                    checkBalanceModal: (chunks: React.ReactNode[]): React.ReactElement =>
                        CheckBalanceModalLink(chunks, {
                            onClick: onCheckBalanceClick,
                            'data-testid': 'faq-check-balance-modal-link',
                        }),
                }}
            />
        ),
        answerPlainText: intl.formatMessage({
            id: 'gift_cards.landing_page.faq.how_do_i_check_my_gift_card_balance.answerPlainText',
            defaultMessage:
                'You can check the balance of your Sur La Table gift card online, in stores, or by calling our Customer Service Center at 1-317-559-2041. To check your balance online, click here, then enter your gift card number and PIN in the "Gift Card Balance" fields.',
        }),
    },
    {
        id: 'gift_cards.landing_page.faq.how_do_i_gift_a_cooking_class_to_someone',
        title: intl.formatMessage({
            id: 'gift_cards.landing_page.faq.how_do_i_gift_a_cooking_class_to_someone.title',
            defaultMessage: 'How do I gift a cooking class to someone?',
        }),
        answer: intl.formatMessage({
            id: 'gift_cards.landing_page.faq.how_do_i_gift_a_cooking_class_to_someone.answer',
            defaultMessage:
                'The best way to gift a cooking class—to ensure the date and time work for the recipient(s)—is by purchasing a gift card. Our e-gift cards make the perfect last-minute present!',
        }),
    },
    {
        id: 'gift_cards.landing_page.faq.where_can_i_find_my_gift_card_number_and_pin',
        title: intl.formatMessage({
            id: 'gift_cards.landing_page.faq.where_can_i_find_my_gift_card_number_and_pin.title',
            defaultMessage: 'Where can I find my gift card number and PIN?',
        }),
        answer: intl.formatMessage({
            id: 'gift_cards.landing_page.faq.where_can_i_find_my_gift_card_number_and_pin.answer',
            defaultMessage:
                'The gift card number is printed on the back of your physical gift card. The PIN is located beneath a scratch-off or peel-off section for security. If you have an e-gift card, both the number and PIN can be found in your email.',
        }),
    },
    {
        id: 'gift_cards.landing_page.faq.can_gift_cards_be_used_for_merchandise_and_cooking_classes',
        title: intl.formatMessage({
            id: 'gift_cards.landing_page.faq.can_gift_cards_be_used_for_merchandise_and_cooking_classes.title',
            defaultMessage: 'Can gift cards be used for merchandise and cooking classes?',
        }),
        answer: intl.formatMessage({
            id: 'gift_cards.landing_page.faq.can_gift_cards_be_used_for_merchandise_and_cooking_classes.answer',
            defaultMessage:
                'Absolutely! All of our gift cards can be used towards the purchase of both merchandise and cooking classes.',
        }),
    },
    {
        id: 'gift_cards.landing_page.faq.what_if_the_intended_recipient_never_received_their_e_gift_card_or_accidentally_deleted_the_email_containing_their_e_gift_card',
        title: intl.formatMessage({
            id: 'gift_cards.landing_page.faq.what_if_the_intended_recipient_never_received_their_e_gift_card_or_accidentally_deleted_the_email_containing_their_e_gift_card.title',
            defaultMessage:
                'What if the intended recipient never received their e-gift card or accidentally deleted the email containing their e-gift card?',
        }),
        answer: (
            <FormattedMessage
                id="gift_cards.landing_page.faq.what_if_the_intended_recipient_never_received_their_e_gift_card_or_accidentally_deleted_the_email_containing_their_e_gift_card.answer"
                defaultMessage="Please call our Customer Service Center at <tel>1-317-559-2041</tel>. Customer service representatives are available from 9am-9pm Eastern M-F and 10am-6pm Eastern Sat and Sun. To resolve the problem, we will require the following information: order number, name of recipient, and email address to which the electronic gift card was sent. Upon researching the problem we will reissue the e-gift card to the desired recipient."
                values={{
                    tel: TelLink,
                }}
            />
        ),
        answerPlainText: intl.formatMessage({
            id: 'gift_cards.landing_page.faq.what_if_the_intended_recipient_never_received_their_e_gift_card_or_accidentally_deleted_the_email_containing_their_e_gift_card.answerPlainText',
            defaultMessage:
                'Please call our Customer Service Center at 1-317-559-2041. Customer service representatives are available from 9am-9pm Eastern M-F and 10am-6pm Eastern Sat and Sun. To resolve the problem, we will require the following information: order number, name of recipient, and email address to which the electronic gift card was sent. Upon researching the problem we will reissue    the e-gift card to the desired recipient.',
        }),
    },
];

export const convertFAQItemsToSchemaItems = (items: FAQItem[]): FAQSchemaItem[] =>
    items.map((item) => {
        let answer: string = '';

        if (item.answerPlainText) {
            answer = item.answerPlainText;
        } else if (typeof item.answer === 'string') {
            answer = item.answer;
        }

        return {
            id: item.id,
            title: item.title,
            answer,
        };
    });
